// HistoriquePointages.jsx — Composant historique des pointages (autonome)
import api from '../services/api';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  Clock, User, CheckCircle, X, Send, History, Users, 
  Search, Check, AlertCircle, AlertTriangle, XCircle, 
  HelpCircle, Fingerprint, ChevronDown, ChevronRight,
  Camera, ZoomIn, Eye, Download, FileText, Filter,
  Loader2Icon
} from 'lucide-react';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { useAuth } from '../contexts/AuthContext';
import GuideHistoriquePointages from './GuideHistoriquePointages';
import Pagination from '../components/ui/Pagination';
import ReactDOM from 'react-dom';
import { formatHeureLocale } from '../utils/timeUtils';
import { API_URL } from '../config/apiBase';


// ============================================================================
// FONCTIONS UTILITAIRES (autonomes)
// ============================================================================

const getStatutPonctualiteInfo = (statut) => {
  const statuts = {
    a_temps: {
      label: 'À temps', icon: CheckCircle, color: 'green',
      bgColor: 'bg-green-50', textColor: 'text-green-700', borderColor: 'border-green-500'
    },
    tolerance: {
      label: 'Tolérance', icon: AlertTriangle, color: 'yellow',
      bgColor: 'bg-yellow-50', textColor: 'text-yellow-700', borderColor: 'border-yellow-500'
    },
    retard: {
      label: 'En retard', icon: Clock, color: 'orange',
      bgColor: 'bg-orange-50', textColor: 'text-orange-700', borderColor: 'border-orange-500'
    },
    retard_grave: {
      label: 'Retard grave', icon: XCircle, color: 'red',
      bgColor: 'bg-red-50', textColor: 'text-red-700', borderColor: 'border-red-500'
    },
    depart_anticipe: {
      label: 'Départ anticipé', icon: AlertTriangle, color: 'yellow',
      bgColor: 'bg-yellow-50', textColor: 'text-yellow-700', borderColor: 'border-yellow-500'
    },
    depart_anticipe_grave: {
      label: 'Départ très anticipé', icon: XCircle, color: 'red',
      bgColor: 'bg-red-50', textColor: 'text-red-700', borderColor: 'border-red-500'
    },
    pause_excessive: {
      label: 'Pause excessive', icon: XCircle, color: 'red',
      bgColor: 'bg-red-50', textColor: 'text-red-700', borderColor: 'border-red-500'
    }
  };
  return statuts[statut] || {
    label: 'N/A', icon: HelpCircle, color: 'gray',
    bgColor: 'bg-gray-50', textColor: 'text-gray-700', borderColor: 'border-gray-500'
  };
};

const getPointageTypeInfo = (type) => {
  const types = {
    arrivee: { label: 'Arrivée', bgColor: 'bg-green-100', textColor: 'text-green-700', dotColor: 'bg-green-500' },
    pause_sortie: { label: 'Sortie Pause', bgColor: 'bg-orange-100', textColor: 'text-orange-700', dotColor: 'bg-orange-500' },
    pause_retour: { label: 'Retour Pause', bgColor: 'bg-purple-100', textColor: 'text-purple-700', dotColor: 'bg-purple-500' },
    depart: { label: 'Départ', bgColor: 'bg-blue-100', textColor: 'text-blue-700', dotColor: 'bg-blue-500' },
    absence: { label: 'Absence', bgColor: 'bg-red-100', textColor: 'text-red-700', dotColor: 'bg-red-500' }
  };
  return types[type] || types.arrivee;
};

const getPhotoUrl = (photoPath) => {
  if (!photoPath) return null;
  const normalized = photoPath.replace(/\\/g, '/');
  // Éviter le double /uploads/uploads/
  if (normalized.startsWith('uploads/') || normalized.startsWith('/uploads/')) {
    return `${API_URL}/${normalized.replace(/^\//, '')}`;
  }
  return `${API_URL}/uploads/${normalized}`;
};

// Définition des filtres de statut disponibles
const STATUT_FILTERS = [
  { key: 'tous', label: 'Tous', icon: Users, bgActive: 'bg-[#448696]', bgInactive: 'bg-gray-100', textActive: 'text-white', textInactive: 'text-gray-600' },
  { key: 'present', label: 'Présents', icon: CheckCircle, bgActive: 'bg-green-500', bgInactive: 'bg-gray-100', textActive: 'text-white', textInactive: 'text-gray-600' },
  { key: 'absent', label: 'Absents', icon: X, bgActive: 'bg-red-500', bgInactive: 'bg-gray-100', textActive: 'text-white', textInactive: 'text-gray-600' },
  { key: 'a_temps', label: 'À temps', icon: CheckCircle, bgActive: 'bg-green-500', bgInactive: 'bg-gray-100', textActive: 'text-white', textInactive: 'text-gray-600' },
  { key: 'retard', label: 'Retards', icon: Clock, bgActive: 'bg-orange-500', bgInactive: 'bg-gray-100', textActive: 'text-white', textInactive: 'text-gray-600' },
  { key: 'retard_grave', label: 'Retards graves', icon: XCircle, bgActive: 'bg-red-500', bgInactive: 'bg-gray-100', textActive: 'text-white', textInactive: 'text-gray-600' },
  { key: 'depart_anticipe', label: 'Départs anticipés', icon: AlertTriangle, bgActive: 'bg-yellow-500', bgInactive: 'bg-gray-100', textActive: 'text-white', textInactive: 'text-gray-600' },
  { key: 'incomplet', label: 'Incomplets', icon: AlertTriangle, bgActive: 'bg-orange-500', bgInactive: 'bg-gray-100', textActive: 'text-white', textInactive: 'text-gray-600' },
  { key: 'ok', label: 'OK', icon: CheckCircle, bgActive: 'bg-emerald-500', bgInactive: 'bg-gray-100', textActive: 'text-white', textInactive: 'text-gray-600' },
  { key: 'justifie', label: 'Justifiés', icon: CheckCircle, bgActive: 'bg-green-500', bgInactive: 'bg-gray-100', textActive: 'text-white', textInactive: 'text-gray-600' },
];


// ============================================================================
// SOUS-COMPOSANT : Popup Zoom Photo
// ============================================================================

const PhotoZoomPopup = ({ photoUrl, employeeName, onClose }) => {
  if (!photoUrl) return null;

  return ReactDOM.createPortal(
    <div 
      className="fixed inset-0 flex items-center justify-center bg-black/80"
      style={{ zIndex: 9999 }}
    >
      <div 
        className="relative max-w-lg w-full mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-white">
            <Camera size={18} />
            <span className="text-sm font-semibold">{employeeName}</span>
          </div>
          <button 
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 text-white transition-all"
          >
            <X size={18} />
          </button>
        </div>

        {/* Photo */}
        <img 
          src={photoUrl} 
          alt={`Photo pointage - ${employeeName}`}
          className="w-full rounded-2xl shadow-2xl"
          style={{ maxHeight: '70vh', objectFit: 'contain' }}
        />
      </div>
    </div>,
     document.body
  );
};


// ============================================================================
// SOUS-COMPOSANT : Miniature Photo
// ============================================================================

const PhotoThumbnail = ({ photoPath, employeeName, onClick }) => {
  const [imgError, setImgError] = useState(false);
  const url = getPhotoUrl(photoPath);
    console.log('📸 photoPath:', photoPath, '| url:', url); 

  if (!photoPath || !url || imgError) {
    return (
      <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center" title="Pas de photo">
        <Camera size={14} className="text-gray-300" />
      </div>
    );
  }

  return (
    <button 
      onClick={() => onClick(url, employeeName)}
      className="relative group w-10 h-10 rounded-lg overflow-hidden border-2 border-gray-200 hover:border-[#448696] transition-all shadow-sm hover:shadow-md"
      title="Voir la photo"
    >
      <img 
        src={url} 
        alt="Photo pointage"
        className="w-full h-full object-cover"
        onError={() => setImgError(true)}
      />
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 flex items-center justify-center transition-all">
        <ZoomIn size={14} className="text-white opacity-0 group-hover:opacity-100 transition-all" />
      </div>
    </button>
  );
};


// ============================================================================
// SOUS-COMPOSANT : Badge Ponctualité
// ============================================================================

const BadgePonctualite = ({ statut }) => {
  if (!statut) return <span className="text-xs text-gray-400">-</span>;
  
  const info = getStatutPonctualiteInfo(statut);
  const StatutIcon = info.icon;
  
  return (
    <span className={`px-2 py-1 text-xs font-semibold rounded-lg inline-flex items-center gap-1 ${info.bgColor} ${info.textColor}`}>
      <StatutIcon size={12} />
      <span>{info.label}</span>
    </span>
  );
};


// ============================================================================
// SOUS-COMPOSANT : Filtre Autocomplete Employé
// ============================================================================

const EmployeeFilter = ({ employees, selectedEmployee, onSelect }) => {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filtered = employees.filter(emp =>
    emp.name.toLowerCase().includes(search.toLowerCase()) ||
    (emp.department || '').toLowerCase().includes(search.toLowerCase()) ||
    (emp.badge_number || '').toLowerCase().includes(search.toLowerCase())
  );

  const handleSelect = (emp) => {
    onSelect(emp);
    setSearch('');
    setIsOpen(false);
  };

  const handleClear = () => {
    onSelect(null);
    setSearch('');
  };

  return (
    <div ref={wrapperRef} className="relative">
      {selectedEmployee ? (
        <div className="flex items-center gap-2 px-4 py-3 bg-[#448696]/10 border-2 border-[#448696] rounded-xl">
          <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: '#448696' }}>
            <User size={14} className="text-white" />
          </div>
          <div className="flex-1">
            <span className="text-sm font-bold text-gray-900">{selectedEmployee.name}</span>
            {selectedEmployee.department && (
              <span className="text-xs text-gray-500 ml-2">• {selectedEmployee.department}</span>
            )}
          </div>
          <button 
            onClick={handleClear}
            className="p-1.5 rounded-lg hover:bg-white/60 text-gray-500 hover:text-gray-700 transition-all"
          >
            <X size={16} />
          </button>
        </div>
      ) : (
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Recherher un employé..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
            className="w-full pl-12 pr-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#448696] font-medium text-sm transition-all"
          />
        </div>
      )}

      {isOpen && !selectedEmployee && (
        <div className="absolute top-full mt-2 left-0 right-0 bg-white rounded-xl shadow-xl border border-gray-100 z-40 max-h-64 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="p-4 text-center text-sm text-gray-500">Aucun employé trouvé</div>
          ) : (
            filtered.map((emp) => (
              <button
                key={emp.id}
                onClick={() => handleSelect(emp)}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-all text-left border-b border-gray-50 last:border-0"
              >
                <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: '#448696' }}>
                  <User size={14} className="text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-gray-900 truncate">{emp.name}</div>
                  <div className="text-xs text-gray-500 truncate">
                    {emp.department || 'N/A'} {emp.badge_number ? `• Badge: ${emp.badge_number}` : ''}
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};


// ============================================================================
// SOUS-COMPOSANT : Bouton Télécharger Rapport (avec sections collapsibles)
// ============================================================================

const DownloadReportButton = ({ departments, onDownload }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleChoice = (type, value) => {
    onDownload(type, value);
    setIsOpen(false);
    setExpandedSections({});
  };

  return (
    <div ref={wrapperRef} className="relative flex-shrink-0">
      <button
        onClick={() => { setIsOpen(!isOpen); setExpandedSections({}); }}
        className="flex items-center gap-2 px-5 py-3 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all text-sm"
        style={{ backgroundColor: '#448696' }}
      >
        <Download size={18} />
        <span className="hidden md:inline">Télécharger</span>
        <ChevronDown size={16} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full mt-2 right-0 bg-white rounded-xl shadow-2xl border border-gray-100 z-40 w-72 overflow-hidden">
          {/* Header */}
          <div className="px-4 py-3" style={{ backgroundColor: '#448696' }}>
            <div className="flex items-center gap-2 text-white">
              <FileText size={16} />
              <span className="text-sm font-bold">Type de rapport</span>
            </div>
          </div>

          <div className="max-h-80 overflow-y-auto">
            {/* Tous les employés — toujours visible */}
            <button
              onClick={() => handleChoice('all', null)}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-all text-left border-b border-gray-100"
            >
              <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ backgroundColor: '#448696' }}>
                <Users size={14} className="text-white" />
              </div>
              <span className="text-sm font-semibold text-gray-800">Tous les employés</span>
            </button>

            {/* Section : Par département (collapsible) */}
            {departments.length > 0 && (
              <div className="border-b border-gray-100">
                <button
                  onClick={() => toggleSection('dept')}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-all"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-7 h-7 rounded-full flex items-center justify-center bg-purple-500">
                      <Users size={14} className="text-white" />
                    </div>
                    <div>
                      <span className="text-sm font-semibold text-gray-800">Par département</span>
                      <span className="text-xs text-gray-400 ml-2">({departments.length})</span>
                    </div>
                  </div>
                  <ChevronRight 
                    size={16} 
                    className={`text-gray-400 transition-transform ${expandedSections.dept ? 'rotate-90' : ''}`} 
                  />
                </button>

                {expandedSections.dept && (
                  <div className="bg-gray-50/50 pb-1">
                    {departments.map(dept => (
                      <button
                        key={dept}
                        onClick={() => handleChoice('department', dept)}
                        className="w-full flex items-center gap-3 pl-10 pr-4 py-2 hover:bg-gray-100 transition-all text-left"
                      >
                        <div className="w-2 h-2 rounded-full bg-purple-400"></div>
                        <span className="text-sm font-medium text-gray-700">{dept}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Section : Par statut (collapsible) */}
            <div className="border-b border-gray-100">
              <button
                onClick={() => toggleSection('status')}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-all"
              >
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center bg-orange-500">
                    <Filter size={14} className="text-white" />
                  </div>
                  <span className="text-sm font-semibold text-gray-800">Par statut</span>
                </div>
                <ChevronRight 
                  size={16} 
                  className={`text-gray-400 transition-transform ${expandedSections.status ? 'rotate-90' : ''}`} 
                />
              </button>

              {expandedSections.status && (
                <div className="bg-gray-50/50 pb-1">

                  {[
                    { key: 'present', label: 'Présents', color: 'bg-green-500', icon: CheckCircle },
                    { key: 'absent', label: 'Absents', color: 'bg-red-500', icon: X },
                    { key: 'retard', label: 'Retards', color: 'bg-orange-500', icon: Clock },
                    { key: 'a_temps', label: 'À temps', color: 'bg-emerald-500', icon: Check },
                    { key: 'incomplet', label: 'Incomplets', color: 'bg-orange-500', icon: AlertTriangle },
                    { key: 'justifie', label: 'Justifiés', color: 'bg-green-500', icon: CheckCircle },
                  ].map(({ key, label, color, icon: Icon }) => (


                    <button
                      key={key}
                      onClick={() => handleChoice('status', key)}
                      className="w-full flex items-center gap-3 pl-10 pr-4 py-2 hover:bg-gray-100 transition-all text-left"
                    >
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center ${color}`}>
                        <Icon size={10} className="text-white" />
                      </div>
                      <span className="text-sm font-medium text-gray-700">{label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
};


// ============================================================================
// SOUS-COMPOSANT : Carte Employé (vue groupée)
// ============================================================================
const EmployeeCard = ({ employeeName, employeeId, department, pointages, onSendToAccounting, onSendLotToAccounting, loading, onPhotoClick, comptaEnabled, 
  isIncomplet, justifications, savingJustification, onJustificationChange, onSaveJustification, typesAttendus, justificationsGlobales, justificationsSauvegardees, photoEnabled, companyTimezone }) => {
  const empId = Number(employeeId) || employeeId;  
  console.log('🔍 [EmployeeCard] employeeId:', employeeId, '| empId:', empId, '| justificationsGlobales:', justificationsGlobales, '| résultat:', justificationsGlobales?.[empId]);
  const [isExpanded, setIsExpanded] = useState(false);

  const [sendingHistorique, setSendingHistorique] = useState(false);

  const [showHistorique, setShowHistorique]       = useState(false);
  const [historiqueData, setHistoriqueData]       = useState([]);
  const [loadingHistorique, setLoadingHistorique] = useState(false);
  const [historiqueError, setHistoriqueError]     = useState(null);
  const [plageRapide, setPlageRapide]             = useState(null); // '7j' | '30j' | '90j' | 'custom'
  const [dateFrom, setDateFrom]                   = useState('');
  const [dateTo, setDateTo]                       = useState('');

  const hasPhoto = pointages.some(p => p.photo_pointage);
  const nbPointages = pointages.length;
  const hasRetard = pointages.some(p => ['retard', 'retard_grave', 'tolerance'].includes(p.statut_ponctualite));
  const isAbsent = pointages.some(p => p.statut === 'absent');
  // Calculer si toutes les justifications requises sont sauvegardées
  const pointagesRetardGrave = pointages.filter(p => 
    p.statut_ponctualite === 'retard_grave' || 
    p.statut_ponctualite === 'depart_anticipe_grave'
  );
  
  const toutesJustificationsSauvegardees = (() => {
    if (!isIncomplet && pointagesRetardGrave.length === 0) return false;
    
    // Vérifier justifications retards graves
    const retardsJustifies = pointagesRetardGrave.every(p => 
      justificationsSauvegardees?.[p.id] !== undefined ||
      (p.observations && p.observations.trim() !== '')
    );
    
    // Vérifier justification globale si incomplet
    const globaleJustifiee = !isIncomplet || (
     justificationsSauvegardees?.[`global_${employeeId}`] !== undefined ||
      !!justificationsGlobales?.[empId]?.texte
    );
    
    return retardsJustifies && globaleJustifiee;
  })();

  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden shadow-sm hover:shadow-md transition-all">
      {/* Header cliquable */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center gap-4 px-5 py-4 hover:bg-gray-50/50 transition-all text-left"
      >
        <div className="text-gray-400">
          {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
        </div>

        <div className="w-10 h-10 rounded-full flex items-center justify-center shadow-sm" 
             style={{ backgroundColor: isAbsent ? '#ef4444' : '#448696' }}>
          <User size={18} className="text-white" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-gray-900 truncate">{employeeName}</div>
          <div className="text-xs text-gray-500">{department || 'N/A'}</div>
        </div>



       <div className="flex items-center gap-2 flex-shrink-0">
          {/* Col 1 — Statut : largeur fixe pour alignement */}
          <div className="w-24 flex justify-end">
            {isAbsent && (
              <span className="px-2 py-1 text-xs font-semibold rounded-lg bg-red-100 text-red-700">Absent</span>
            )}
            {isIncomplet && !isAbsent && !toutesJustificationsSauvegardees && (
              <span className="px-2 py-1 text-xs font-semibold rounded-lg bg-orange-100 text-orange-700">⚠ Incomplet</span>
            )}
            {isIncomplet && !isAbsent && toutesJustificationsSauvegardees && (
              <span className="px-2 py-1 text-xs font-semibold rounded-lg bg-green-100 text-green-700">✓ Justifié</span>
            )}
            {hasRetard && !isAbsent && !isIncomplet && (
              <span className="px-2 py-1 text-xs font-semibold rounded-lg bg-orange-100 text-orange-700">Retard</span>
            )}
            {!hasRetard && !isAbsent && !isIncomplet && nbPointages > 0 && (
              <span className="px-2 py-1 text-xs font-semibold rounded-lg bg-green-100 text-green-700">OK</span>
            )}
          </div>

          {/* Col 2 — Photo : largeur fixe */}
          <div className="w-16 flex justify-end">
            {hasPhoto && (
              <span className="px-2 py-1 text-xs font-semibold rounded-lg bg-blue-50 text-blue-600 flex items-center gap-1">
                <Camera size={12} />Photo
              </span>
            )}
          </div>

          {/* Col 3 — Nb pointages : largeur fixe */}
          <div className="w-24 flex justify-end">
            <span className="px-2 py-1 text-xs font-medium rounded-lg bg-gray-100 text-gray-600">
              {nbPointages} pointage{nbPointages > 1 ? 's' : ''}
            </span>
          </div>
        </div>


      </button>


      {/* Bouton Envoyer groupé — affiché si au moins 1 pointage en attente */}
      {/* 🔥 Bouton Envoyer groupé — Conditionnel (Compta Module) */}
      {comptaEnabled && pointages.some(p => p.statut_comptabilite !== 'envoye') && (
        <div className="px-5 pb-3 -mt-1 flex justify-end">
          <button
           onClick={(e) => {
              e.stopPropagation();
              const ids = pointages.filter(p => p.statut_comptabilite !== 'envoye').map(p => p.id);
              onSendLotToAccounting(ids);
            }}
            disabled={loading}
            className="text-xs px-4 py-2 text-white rounded-lg font-semibold transition-all shadow-sm hover:shadow flex items-center gap-1.5"
            style={{ backgroundColor: '#6366f1' }}
          >
            <Send size={14} />
            Envoi unique ({pointages.filter(p => p.statut_comptabilite !== 'envoye').length})
          </button>
        </div>
      )}




      {/* Contenu déplié */}
      {isExpanded && (
        <div className="border-t border-gray-100" style={{ width: '100%', overflow: 'visible' }}>
          <table key={comptaEnabled ? 'with-compta' : 'without-compta'} className="w-full">

          <thead className="bg-gray-50/80">
                <tr>
                  <th className="px-5 py-2.5 text-left text-xs font-semibold text-gray-600">Type</th>
                  <th className="px-5 py-2.5 text-left text-xs font-semibold text-gray-600">Heure</th>
                  <th className="px-5 py-2.5 text-left text-xs font-semibold text-gray-600">Statut</th>
                  <th className="px-5 py-2.5 text-left text-xs font-semibold text-gray-600">Ponctualité</th>
                 {photoEnabled && (
                    <th className="px-5 py-2.5 text-left text-xs font-semibold text-gray-600">Photo</th>
                  )}
                  <th className="px-5 py-2.5 text-left text-xs font-semibold text-gray-600">Enregistré par</th>
                  {comptaEnabled && (
                    <th className="px-5 py-2.5 text-left text-xs font-semibold text-gray-600">Comptabilité</th>
                  )}
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-50">
                {pointages.map((pointage) => {
                  const typeInfo = getPointageTypeInfo(pointage.type_pointage);
                  
                  return (
                    <tr key={pointage.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-5 py-3">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-lg ${typeInfo.bgColor} ${typeInfo.textColor}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${typeInfo.dotColor}`}></span>
                          {typeInfo.label}
                        </span>
                      </td>

                    <td className="px-5 py-3">
                        <span className="text-sm font-bold text-gray-900">
                          {formatHeureLocale(pointage.heure_utc, companyTimezone)}
                        </span>
                      </td>

                      <td className="px-5 py-3">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-lg ${
                          pointage.statut === 'present' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {pointage.statut === 'present' ? '✓ Présent' : '✗ Absent'}
                        </span>
                      </td>

                      <td className="px-5 py-3">
                        <BadgePonctualite statut={pointage.statut_ponctualite} />
                      </td>


                      {photoEnabled && (
                        <td className="px-5 py-3">
                          <PhotoThumbnail 
                            photoPath={pointage.photo_pointage} 
                            employeeName={employeeName}
                            onClick={onPhotoClick}
                          />
                        </td>
                      )}


                      <td className="px-5 py-3">
                        {pointage.created_by_name ? (
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-full flex items-center justify-center bg-gray-100">
                              <User size={11} className="text-gray-500" />
                            </div>
                            <span className="text-xs font-medium text-gray-700">{pointage.created_by_name}</span>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </td>

                     {comptaEnabled && (
                        <td className="px-5 py-3">
                          <div className="space-y-1">
                            <span className={`px-2 py-1 text-xs font-semibold rounded-lg ${
                              pointage.statut_comptabilite === 'envoye' ? 'bg-blue-100 text-blue-700' : 'bg-yellow-100 text-yellow-700'
                            }`}>
                              {pointage.statut_comptabilite === 'envoye' ? '✓ Envoyé' : 'En attente'}
                            </span>
                            {pointage.sent_by_name && (
                              <div className="flex items-center gap-1 mt-1">
                                <Send size={10} className="text-gray-400" />
                                <span className="text-[10px] text-gray-500">{pointage.sent_by_name}</span>
                              </div>
                            )}
                          </div>
                        </td>
                      )}

                    
                    </tr>
                  );
                })}
              </tbody>
            </table>
       


     {/* Observations existantes */}
          {(pointages.some(p => p.observations) || justificationsGlobales?.[empId]?.texte || justificationsSauvegardees?.[`global_${employeeId}`]) && (
            <div className="px-5 py-3 bg-gray-50/50 border-t border-gray-100 space-y-1">


            {pointages.map(p => {
                const obsLocale = justificationsSauvegardees?.[p.id]?.texte;
                const obs = obsLocale || p.observations;
                if (!obs) return null;
                return (
                  <div key={p.id} className="text-xs text-gray-600">
                    <span className="font-semibold">{getPointageTypeInfo(p.type_pointage).label} :</span> {obs}
                  </div>
                );
              })}

              {/* Justification globale */}
              {(justificationsGlobales?.[empId]?.texte || justificationsSauvegardees?.[`global_${employeeId}`]?.texte) && (
                <div className="text-xs text-gray-600">
                  <span className="font-semibold">Justification globale :</span>{' '}
                  {justificationsSauvegardees?.[`global_${employeeId}`]?.texte || justificationsGlobales?.[empId]?.texte}
                </div>
              )}
            </div>
          )}


         {(isIncomplet || pointagesRetardGrave.length > 0) && (
            <div className={`px-5 py-4 border-t-2 ${
              toutesJustificationsSauvegardees 
                ? 'border-green-200 bg-green-50/50' 
                : 'border-orange-200 bg-orange-50/50'
            }`}>
              


            {toutesJustificationsSauvegardees ? (
                /* Message justification complétée */
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <CheckCircle size={18} className="text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-green-800">Justification complétée</p>
                    <p className="text-xs text-green-600 mt-0.5">Toutes les anomalies ont été justifiées</p>
                    {/* Infos de qui a justifié */}
                    {(justificationsGlobales?.[empId]?.created_by_name || pointagesRetardGrave.some(p => p.observations)) && (
                      <div className="mt-2 flex items-center gap-1.5 text-xs text-green-700">
                     
                        <span>
                          Justifié par <strong>
                            {justificationsGlobales?.[empId]?.created_by_name || 
                             pointagesRetardGrave.find(p => p.observations)?.created_by_name || 
                             'Inconnu'}
                          </strong>
                          {justificationsGlobales?.[empId]?.updated_at && (
                            <span className="ml-1">
                              · {new Date(justificationsGlobales[empId].updated_at).toLocaleString('fr-FR', {
                                day: '2-digit', month: '2-digit', year: 'numeric',
                                hour: '2-digit', minute: '2-digit'
                              })}
                            </span>
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                </div>


              ) : (
                /* Formulaire de justification */
                <>
                  <div className="flex items-center gap-2 mb-3">
                    
                    <span className="text-sm font-bold text-orange-800">
                      {isIncomplet ? 'Pointages incomplets' : 'Anomalie détectée'} - Justification requise
                    </span>
                  </div>

                  {/* Tags des types manquants */}
                  {isIncomplet && typesAttendus && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {typesAttendus.map(type => {
                        const typesPresents = pointages.map(p => p.type_pointage);
                        const manque = !typesPresents.includes(type);
                        const typeInfo = getPointageTypeInfo(type);
                        return (
                          <span
                            key={type}
                            className={`px-2 py-1 text-xs font-semibold rounded-lg flex items-center gap-1 ${
                              manque ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                            }`}
                          >
                            {manque ? '✗' : '✓'} {typeInfo.label}
                          </span>
                        );
                      })}
                    </div>
                  )}

                  <div className="space-y-3">
                    {/* Justifications retards graves */}
                    {pointagesRetardGrave.map(p => {


                     const justSauvegardee = justificationsSauvegardees?.[p.id];
                      const estSauvegarde = justSauvegardee !== undefined ||
                        (p.observations && p.observations.trim() !== '');


                      return (
                        <div key={p.id}>

                          <label className="text-xs font-semibold text-gray-600 block mb-1">
                            Justification - {getPointageTypeInfo(p.type_pointage).label}
                            {p.statut_ponctualite === 'retard_grave' && ' (retard grave)'}
                            {p.statut_ponctualite === 'depart_anticipe_grave' && ' (départ très anticipé)'}
                          </label>


                          {estSauvegarde && (
                                <div className="flex items-center gap-2 mb-1 text-xs text-green-700 bg-green-50 px-2 py-1 rounded-lg border border-green-200">
                                  <CheckCircle size={12} className="flex-shrink-0" />
                                  <span>
                                    Enregistrée par <strong>
                                      {justSauvegardee?.created_by_name || p.created_by_name || 'Inconnu'}
                                    </strong>
                                    {justSauvegardee?.saved_at && (
                                      <span className="ml-1 text-green-600">
                                        · {new Date(justSauvegardee.saved_at).toLocaleString('fr-FR', {
                                          day: '2-digit', month: '2-digit', year: 'numeric',
                                          hour: '2-digit', minute: '2-digit'
                                        })}
                                      </span>
                                    )}
                                  </span>
                                </div>
                              )}


                          <div className="flex gap-2">
                            <textarea
                              rows={2}
                              placeholder="Saisir une justification..."
                              value={justifications[p.id] !== undefined ? justifications[p.id] : (p.observations || '')}
                              onChange={(e) => !estSauvegarde && onJustificationChange(p.id, e.target.value)}
                              disabled={estSauvegarde}
                              className={`flex-1 px-3 py-2 text-sm border rounded-lg focus:outline-none resize-none transition-all ${
                                estSauvegarde 
                                  ? 'bg-gray-100 border-gray-200 text-gray-500 cursor-not-allowed' 
                                  : 'bg-white border-orange-200 focus:border-orange-400'
                              }`}
                            />
                            {!estSauvegarde && (
                              <button
                                onClick={() => onSaveJustification(employeeId, p.id, justifications[p.id] !== undefined ? justifications[p.id] : (p.observations || ''))}
                                disabled={savingJustification[p.id]}
                                className="px-3 py-2 text-white text-xs font-semibold rounded-lg transition-all self-end disabled:opacity-50"
                                style={{ backgroundColor: '#448696' }}
                              >
                                {savingJustification[p.id] ? '...' : 'Enregistrer'}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}

                    {/* Justification globale si incomplet */}
                    {isIncomplet && (() => {


                   const justGlobaleSauvegardee = justificationsSauvegardees?.[`global_${employeeId}`];
                    const estSauvegarde = justGlobaleSauvegardee !== undefined ||
                    !!justificationsGlobales?.[empId]?.texte

                      return (
                        <div>

                         <label className="text-xs font-semibold text-gray-600 block mb-1">
                            Justification globale - pointages manquants
                          </label>

                            {estSauvegarde && (
                            <div className="flex items-center gap-2 mb-1 text-xs text-green-700 bg-green-50 px-2 py-1 rounded-lg border border-green-200">
                              <CheckCircle size={12} className="flex-shrink-0" />
                              <span>
                                Enregistrée par <strong>
                                  {justGlobaleSauvegardee?.created_by_name || 
                                   justificationsGlobales?.[empId]?.created_by_name || 
                                   'Inconnu'}
                                </strong>
                                {(justGlobaleSauvegardee?.saved_at || justificationsGlobales?.[empId]?.updated_at) && (
                                  <span className="ml-1 text-green-600">
                                    · {new Date(justGlobaleSauvegardee?.saved_at || justificationsGlobales[empId]?.updated_at).toLocaleString('fr-FR', {
                                      day: '2-digit', month: '2-digit', year: 'numeric',
                                      hour: '2-digit', minute: '2-digit'
                                    })}
                                  </span>
                                )}
                              </span>
                            </div>
                          )}


                          <div className="flex gap-2">
                            <textarea
                              rows={2}
                              placeholder="Ex: Problème d'internet ..."


                              value={justifications[`global_${employeeId}`] !== undefined 
                              ? justifications[`global_${employeeId}`] 
                              : (justificationsGlobales?.[empId]?.texte || '')}


                              onChange={(e) => !estSauvegarde && onJustificationChange(`global_${employeeId}`, e.target.value)}
                              disabled={estSauvegarde}
                              className={`flex-1 px-3 py-2 text-sm border rounded-lg focus:outline-none resize-none transition-all ${
                                estSauvegarde 
                                  ? 'bg-gray-100 border-gray-200 text-gray-500 cursor-not-allowed' 
                                  : 'bg-white border-orange-200 focus:border-orange-400'
                              }`}
                            />
                            {!estSauvegarde && (
                              <button
                                onClick={() => onSaveJustification(employeeId, `global_${employeeId}`, justifications[`global_${employeeId}`] || '')}
                                disabled={savingJustification[`global_${employeeId}`]}
                                className="px-3 py-2 text-white text-xs font-semibold rounded-lg transition-all self-end disabled:opacity-50"
                                style={{ backgroundColor: '#448696' }}
                              >
                                {savingJustification[`global_${employeeId}`] ? '...' : 'Enregistrer'}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </>
              )}
            </div>
          )}

          
          {/* ── HISTORIQUE INDIVIDUEL ── */}
          <div className="border-t-2 border-dashed border-gray-200 mx-4 my-3" />
          <div className="px-5 pb-5">

        
         {/* Titre + tooltip */}
          <div className="mb-4">
              <div className="flex items-center gap-2">
               
                <span className="text-sm font-bold text-gray-700">Historique de cet employé</span>
                <div className="relative group ml-1">
                  <button className="w-5 h-5 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 transition-all"
                    style={{ backgroundColor: '#f0f9fb', border: '1px solid #c8dde2' }}>
                    <HelpCircle size={12} />
                  </button>
                  <div className="absolute left-0 bottom-full mb-2 w-72 hidden group-hover:block z-50">
                    <div className="px-4 py-3 rounded-lg text-xs text-gray-600 leading-relaxed shadow-xl bg-white"
                      style={{ border: '1px solid #e5e7eb' }}>
                      <p className="font-semibold text-gray-800 mb-1.5">Historique individuel</p>
                      Sélectionnez une période pour charger les pointages passés de cet employé.
                      Les <strong>sélections rapides</strong> calculent à partir d'aujourd'hui en remontant :
                      7j, 30j, 90j. Pour une plage précise, utilisez <strong>Personnalisé</strong>.
                      <span className="block mt-1.5 text-gray-400">Maximum <strong>90 jours</strong> par chargement.</span>
                    </div>
                    {/* Flèche vers le bas */}
                    <div className="absolute top-full left-3 w-0 h-0"
                      style={{ borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop: '5px solid #e5e7eb' }} />
                  </div>
                </div>
              </div>
            </div>

            {/* Plages rapides */}
            <div className="flex items-center gap-2 flex-wrap mb-3">
              {[
                { label: '7 derniers jours',  value: '7j',  days: 7  },
                { label: '30 derniers jours', value: '30j', days: 30 },
                { label: '90 derniers jours', value: '90j', days: 90 },
              ].map(({ label, value, days }) => {
                const today = new Date();
                const from  = new Date(today);
                from.setDate(today.getDate() - days);
                const fmt     = (d) => d.toISOString().split('T')[0];
                const fromFmt = from.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
                const toFmt   = today.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });

                return (
                  <div key={value} className="relative group">
                    <button
                      onClick={() => {
                        if (plageRapide === value) {
                          setPlageRapide(null);
                          setShowHistorique(false);
                          setHistoriqueData([]);
                          setHistoriqueError(null);
                          return;
                        }
                        setPlageRapide(value);
                        setHistoriqueError(null);
                        setShowHistorique(false);
                        setDateFrom(fmt(from));
                        setDateTo(fmt(today));
                      }}
                      className="px-3 py-2 rounded-xl text-xs font-bold border-2 transition-all flex items-center gap-1.5"
                      style={{
                        backgroundColor: plageRapide === value ? '#448696' : 'white',
                        color: plageRapide === value ? 'white' : '#6b7280',
                        borderColor: plageRapide === value ? '#448696' : '#e5e7eb'
                      }}>
                  
                      {label}
                    </button>
                    {/* Tooltip dates au survol */}
                    <div className="absolute bottom-full left-0 mb-1.5 hidden group-hover:flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium text-white shadow-lg whitespace-nowrap z-50"
                      style={{ backgroundColor: '#2d6a7f' }}>
                      {fromFmt} - {toFmt}
                      <div className="absolute top-full left-4 w-0 h-0"
                        style={{ borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop: '5px solid #2d6a7f' }} />
                    </div>
                  </div>
                );
              })}

              {/* Personnalisé */}
              <button
                onClick={() => {
                  if (plageRapide === 'custom') {
                    setPlageRapide(null);
                    setShowHistorique(false);
                    setHistoriqueData([]);
                    setHistoriqueError(null);
                    return;
                  }
                  setPlageRapide('custom');
                  setDateFrom('');
                  setDateTo('');
                  setShowHistorique(false);
                  setHistoriqueError(null);
                }}
                className="px-3 py-2 rounded-xl text-xs font-bold border-2 transition-all flex items-center gap-1.5"
                style={{
                  backgroundColor: plageRapide === 'custom' ? '#448696' : 'white',
                  color: plageRapide === 'custom' ? 'white' : '#6b7280',
                  borderColor: plageRapide === 'custom' ? '#448696' : '#e5e7eb'
                }}>
                Personnalisé
              </button>

              {/* Fermer l'historique */}
              {showHistorique && (
                <button
                  onClick={() => {
                    setShowHistorique(false);
                    setHistoriqueData([]);
                    setPlageRapide(null);
                    setHistoriqueError(null);
                  }}
                  className="px-3 py-2 rounded-xl text-xs font-bold border-2 border-red-200 text-red-500 hover:bg-red-50 transition-all flex items-center gap-1.5">
                  <X size={11} /> Fermer l'historique
                </button>
              )}
            </div>

            {/* Champs dates personnalisées */}
            {plageRapide === 'custom' && (
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 font-semibold">Du</span>
                  <input type="date" value={dateFrom}
                    onChange={e => { setDateFrom(e.target.value); setHistoriqueError(null); }}
                    className="px-3 py-2 border-2 border-gray-200 rounded-lg text-xs font-medium focus:outline-none transition-all"
                    style={{ borderColor: dateFrom ? '#448696' : '#e5e7eb' }}
                    max={dateTo || undefined}
                  />
                </div>
                <span className="text-xs text-gray-400 font-bold"></span>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 font-semibold">Au</span>
                  <input type="date" value={dateTo}
                    onChange={e => { setDateTo(e.target.value); setHistoriqueError(null); }}
                    className="px-3 py-2 border-2 border-gray-200 rounded-lg text-xs font-medium focus:outline-none transition-all"
                    style={{ borderColor: dateTo ? '#448696' : '#e5e7eb' }}
                    min={dateFrom || undefined}
                  />
                </div>
              </div>
            )}

            {/* Message d'erreur */}
            {historiqueError && (
              <div className="mb-3 px-3 py-2.5 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700 font-medium flex items-start gap-2">
                <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" />
                <span>
                  {historiqueError}
                  {historiqueError.includes('90 jours') && (
                    <span className="block mt-0.5 text-red-500 font-normal">
                      Utilisez le calendrier en haut de la page pour accéder aux dates plus anciennes.
                    </span>
                  )}
                </span>
              </div>
            )}


            {/* Bouton charger */}
            {plageRapide && (
              <div className="mb-4 flex items-center gap-3">
                {/* Plage sélectionnée — texte à gauche */}
                {dateFrom && dateTo && (
                  <p className="text-xs text-gray-500">
                    <span className="font-bold text-gray-700">Période sélectionnée :</span>{' '}
                    {new Date(dateFrom + 'T00:00:00').toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}
                    {' - '}
                    {new Date(dateTo + 'T00:00:00').toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}
                  </p>
                )}

                {/* Bouton compact */}
                <button
                  onClick={async () => {
                    if (!dateFrom || !dateTo) return;
                    const diffDays = Math.ceil((new Date(dateTo) - new Date(dateFrom)) / (1000 * 60 * 60 * 24));
                    if (diffDays > 92) {
                      setHistoriqueError('La plage maximale est de 90 jours. Utilisez le calendrier en haut pour les dates plus anciennes.');
                      return;
                    }
                    setLoadingHistorique(true);
                    setHistoriqueError(null);
                    setShowHistorique(false);
                    try {
                      const res = await api.pointages.getHistoriqueEmploye(employeeId, dateFrom, dateTo);
                      if (res.success) {
                        setHistoriqueData(res.data);
                        setShowHistorique(true);
                      } else {
                        setHistoriqueError(res.message || 'Erreur lors du chargement');
                      }
                    } catch (e) {
                      setHistoriqueError('Erreur lors du chargement de l\'historique');
                    } finally {
                      setLoadingHistorique(false);
                    }
                  }}
                  disabled={!dateFrom || !dateTo || loadingHistorique}
                className="px-3 py-2 text-white text-xs font-semibold rounded-lg transition-all disabled:opacity-50 flex items-center gap-1.5"
                  style={{ backgroundColor: '#448696' }}>
                  {loadingHistorique ? (
                    <><div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" /> Chargement...</>
                  ) : (
                    <>Charger</>
                  )}
                </button>
              </div>
            )}
           
            {/* Résultats — même design que tableau principal */}
            {showHistorique && (
              <div className="space-y-3">
                {historiqueData.length === 0 ? (
                  <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-xl">
                    <History size={28} className="mx-auto mb-2 text-gray-300" />
                    <p className="text-sm text-gray-400 font-semibold">Aucun pointage sur cette période</p>
                  </div>
                ) : (

                <>
                    {/* Barre résumé + bouton envoi */}
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-gray-500 font-semibold">
                        <span className="font-black text-gray-800">{historiqueData.length}</span> jour{historiqueData.length > 1 ? 's' : ''} avec pointages
                        <span className="ml-2 text-gray-400">
                          · {new Date(dateFrom + 'T00:00:00').toLocaleDateString('fr-FR')} - {new Date(dateTo + 'T00:00:00').toLocaleDateString('fr-FR')}
                        </span>
                      </p>

                      {/* Bouton envoi tout */}
                      {comptaEnabled && (() => {
                        const tousPointages = historiqueData.flatMap(d => d.pointages);
                        const enAttente = tousPointages.filter(p => p.statut_comptabilite !== 'envoye');
                        if (enAttente.length === 0) return null;
                        return (
                          <button
                            onClick={async () => {
                              setSendingHistorique(true);
                              try {
                                await onSendLotToAccounting(enAttente.map(p => p.id));
                                // Rafraîchir les données localement
                                setHistoriqueData(prev => prev.map(day => ({
                                  ...day,
                                  pointages: day.pointages.map(p =>
                                    enAttente.find(e => e.id === p.id)
                                      ? { ...p, statut_comptabilite: 'envoye' }
                                      : p
                                  )
                                })));
                              } catch (e) {
                                console.error(e);
                              } finally {
                                setSendingHistorique(false);
                              }
                            }}
                            disabled={sendingHistorique}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-white text-xs font-semibold rounded-lg transition-all disabled:opacity-50"
                            style={{ backgroundColor: '#6366f1' }}
                          >
                            {sendingHistorique ? (
                              <><div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" /> Envoi...</>
                            ) : (
                              <><Send size={12} /> Envoyer tout ({enAttente.length} en attente)</>
                            )}
                          </button>
                        );
                      })()}
                    </div>


                    {historiqueData.map(({ date, pointages: pts }) => (
                      <div key={date} className="rounded-xl border border-gray-100 overflow-hidden shadow-sm">

                        {/* Header date */}
                        <div className="px-5 py-2.5 flex items-center gap-2"
                          style={{ background: 'linear-gradient(135deg, #f0f9fb, #e8f4f7)' }}>
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#448696' }} />
                          <span className="text-xs font-bold" style={{ color: '#448696' }}>
                            {new Date(date + 'T00:00:00').toLocaleDateString('fr-FR', {
                              weekday: 'long', day: '2-digit', month: 'long', year: 'numeric'
                            })}
                          </span>
                          <span className="ml-auto px-2 py-0.5 rounded-full text-xs font-bold"
                            style={{ backgroundColor: '#44869620', color: '#448696' }}>
                            {pts.length} pointage{pts.length > 1 ? 's' : ''}
                          </span>
                        </div>

                        {/* Tableau identique au tableau principal */}
                        <table className="w-full">
                          <thead className="bg-gray-50/80">
                            <tr>
                              <th className="px-5 py-2.5 text-left text-xs font-semibold text-gray-600">Type</th>
                              <th className="px-5 py-2.5 text-left text-xs font-semibold text-gray-600">Heure</th>
                              <th className="px-5 py-2.5 text-left text-xs font-semibold text-gray-600">Statut</th>
                              <th className="px-5 py-2.5 text-left text-xs font-semibold text-gray-600">Ponctualité</th>
                              {photoEnabled && (
                                <th className="px-5 py-2.5 text-left text-xs font-semibold text-gray-600">Photo</th>
                              )}
                              <th className="px-5 py-2.5 text-left text-xs font-semibold text-gray-600">Enregistré par</th>
                              {comptaEnabled && (
                                <th className="px-5 py-2.5 text-left text-xs font-semibold text-gray-600">Comptabilité</th>
                              )}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-50">
                            {pts.map(p => {
                              const typeInfo = getPointageTypeInfo(p.type_pointage);
                              return (
                                <tr key={p.id} className="hover:bg-gray-50/50 transition-colors">
                                  <td className="px-5 py-3">
                                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-lg ${typeInfo.bgColor} ${typeInfo.textColor}`}>
                                      <span className={`w-1.5 h-1.5 rounded-full ${typeInfo.dotColor}`} />
                                      {typeInfo.label}
                                    </span>
                                  </td>

                                  <td className="px-5 py-3">
                                    <span className="text-sm font-bold text-gray-900">
                                      {formatHeureLocale(p.heure_utc, companyTimezone)}
                                    </span>
                                  </td>

                                  <td className="px-5 py-3">
                                    <span className={`px-2 py-1 text-xs font-semibold rounded-lg ${
                                      p.statut === 'present' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                    }`}>
                                      {p.statut === 'present' ? '✓ Présent' : '✗ Absent'}
                                    </span>
                                  </td>
                                  <td className="px-5 py-3">
                                    <BadgePonctualite statut={p.statut_ponctualite} />
                                  </td>
                                  {photoEnabled && (
                                    <td className="px-5 py-3">
                                      <PhotoThumbnail
                                        photoPath={p.photo_pointage}
                                        employeeName={employeeName}
                                        onClick={onPhotoClick}
                                      />
                                    </td>
                                  )}
                                  <td className="px-5 py-3">
                                    {p.created_by_name ? (
                                      <div className="flex items-center gap-2">
                                        <div className="w-6 h-6 rounded-full flex items-center justify-center bg-gray-100">
                                          <User size={11} className="text-gray-500" />
                                        </div>
                                        <span className="text-xs font-medium text-gray-700">{p.created_by_name}</span>
                                      </div>
                                    ) : (
                                      <span className="text-xs text-gray-400">—</span>
                                    )}
                                  </td>
                                  {comptaEnabled && (
                                    <td className="px-5 py-3">
                                      <span className={`px-2 py-1 text-xs font-semibold rounded-lg ${
                                        p.statut_comptabilite === 'envoye'
                                          ? 'bg-blue-100 text-blue-700'
                                          : 'bg-yellow-100 text-yellow-700'
                                      }`}>
                                        {p.statut_comptabilite === 'envoye' ? '✓ Envoyé' : 'En attente'}
                                      </span>
                                    </td>
                                  )}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>


    

        </div>
      )}
    </div>
  );
};


// ============================================================================
// COMPOSANT PRINCIPAL : HistoriquePointages
// ============================================================================


const HistoriquePointages = ({ 
  pointagesHistorique, 
  statsHistorique, 
  onSendToAccounting,
  onSendLotToAccounting,
  loading,
  loadingData,
  selectedDate, 
  showToast,
  modePause = 'automatique',
  paginationInfo = { total: 0, totalPages: 1 },
  pageCourante = 1,
  onPageChange,
  onSearchChange,
  onDepartmentChange
}) => {


  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [statutFilter, setStatutFilter] = useState('tous');
  const [zoomPhoto, setZoomPhoto] = useState(null);

  const [justifications, setJustifications] = useState({});
  const [savingJustification, setSavingJustification] = useState({});
  const [justificationsGlobales, setJustificationsGlobales] = useState({});
  const [justificationsSauvegardees, setJustificationsSauvegardees] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  const [showGuide, setShowGuide] = useState(false);
 const { user, company } = useAuth();
  const companyTimezone = company?.timezone || 'Africa/Brazzaville';


  useEffect(() => {
    const loadJustificationsGlobales = async () => {
      try {
        const response = await api.pointages.getJustificationsGlobales(selectedDate);
        if (response.success) {


         const map = {};
          response.data.forEach(j => {
            // Stocker avec les deux types (string et number) pour éviter les problèmes de type
            map[j.employee_id] = {
              texte: j.justification,
              created_by_name: j.created_by_name,
              updated_at: j.updated_at
            };
            map[String(j.employee_id)] = map[j.employee_id];
          });
          setJustificationsGlobales(map);
        }


        
      } catch (error) {
        console.error('Erreur chargement justifications:', error);
      }
    };
    if (selectedDate) loadJustificationsGlobales();
  }, [selectedDate]);


  const typesAttendus = modePause === 'pointage' 
    ? ['arrivee', 'pause_sortie', 'pause_retour', 'depart']
    : ['arrivee', 'depart'];

  const isEmployeeIncomplet = (pointages) => {
    if (pointages.some(p => p.statut === 'absent')) return false;
    const typesPresents = pointages.map(p => p.type_pointage);
    return typesAttendus.some(t => !typesPresents.includes(t));
  };

  // 🔥 Feature Flag Compta
  const { isEnabled: comptaEnabled } = useFeatureFlag('compta_module');
  const { isEnabled: photoEnabled } = useFeatureFlag('pointage_photo');

  // Extraire la liste unique des employés depuis les pointages
  const employeesList = useMemo(() => {
    const map = new Map();
    pointagesHistorique.forEach(p => {
      if (!map.has(p.employee_id)) {
        map.set(p.employee_id, {
          id: p.employee_id,
          name: p.employee_name,
          department: p.department,
          badge_number: p.badge_number
        });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [pointagesHistorique]);

  // Extraire les départements uniques
  const departments = useMemo(() => {
    return [...new Set(pointagesHistorique.map(p => p.department).filter(Boolean))].sort();
  }, [pointagesHistorique]);

  // Filtrer les pointages
  const filteredPointages = useMemo(() => {
    let result = pointagesHistorique;

    if (selectedEmployee) {
      result = result.filter(p => p.employee_id === selectedEmployee.id);
    }

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(p =>
        p.employee_name.toLowerCase().includes(term) ||
        (p.department || '').toLowerCase().includes(term) ||
        (p.badge_number || '').toLowerCase().includes(term)
      );
    }


    
   // Filtre par statut
    if (statutFilter !== 'tous') {
      if (statutFilter === 'present') {
        result = result.filter(p => p.statut === 'present');
      } else if (statutFilter === 'absent') {
        result = result.filter(p => p.statut === 'absent');
      } else if (statutFilter === 'retard') {
        result = result.filter(p => ['retard', 'retard_grave', 'tolerance'].includes(p.statut_ponctualite));
      } else if (statutFilter === 'retard_grave') {
        result = result.filter(p => p.statut_ponctualite === 'retard_grave');
      } else if (statutFilter === 'a_temps') {
        result = result.filter(p => p.statut_ponctualite === 'a_temps');
      } else if (statutFilter === 'ok') {
        // Employés sans retard, sans absence, sans incomplet
        const employesOk = new Set();
        const tempGroups = new Map();
        result.forEach(p => {
          if (!tempGroups.has(p.employee_id)) tempGroups.set(p.employee_id, []);
          tempGroups.get(p.employee_id).push(p);
        });
        tempGroups.forEach((pointages, empId) => {
          const hasRetard = pointages.some(p => ['retard', 'retard_grave', 'tolerance', 'depart_anticipe', 'depart_anticipe_grave', 'pause_excessive'].includes(p.statut_ponctualite));
          const hasAbsent = pointages.some(p => p.statut === 'absent');
          const isIncomplet = isEmployeeIncomplet(pointages);
          if (!hasRetard && !hasAbsent && !isIncomplet) employesOk.add(empId);
        });
        result = result.filter(p => employesOk.has(p.employee_id));
      } else if (statutFilter === 'depart_anticipe') {
        result = result.filter(p => ['depart_anticipe', 'depart_anticipe_grave'].includes(p.statut_ponctualite));
      
    } else if (statutFilter === 'incomplet') {
        const employesIncomplets = new Set();
        const tempGroups = new Map();
        result.forEach(p => {
          if (!tempGroups.has(p.employee_id)) tempGroups.set(p.employee_id, []);
          tempGroups.get(p.employee_id).push(p);
        });
        tempGroups.forEach((pointages, empId) => {
          if (isEmployeeIncomplet(pointages)) employesIncomplets.add(empId);
        });
        result = result.filter(p => employesIncomplets.has(p.employee_id));
      } else if (statutFilter === 'justifie') {
        const employesJustifies = new Set();
        const tempGroups = new Map();
        result.forEach(p => {
          if (!tempGroups.has(p.employee_id)) tempGroups.set(p.employee_id, []);
          tempGroups.get(p.employee_id).push(p);
        });
        tempGroups.forEach((pointages, empId) => {
          if (!isEmployeeIncomplet(pointages)) return;
          const pointagesRetardGrave = pointages.filter(p => 
            p.statut_ponctualite === 'retard_grave' || 
            p.statut_ponctualite === 'depart_anticipe_grave'
          );
          const retardsJustifies = pointagesRetardGrave.every(p => 
            justificationsSauvegardees?.[p.id] !== undefined ||
            (p.observations && p.observations.trim() !== '')
          );


        const globaleJustifiee = justificationsSauvegardees?.[`global_${empId}`] !== undefined ||
            !!justificationsGlobales?.[empId]?.texte;
          if (retardsJustifies && globaleJustifiee) employesJustifies.add(empId);



        });
        result = result.filter(p => employesJustifies.has(p.employee_id));
      }
    }

    return result;


  }, [pointagesHistorique, selectedEmployee, searchTerm, statutFilter]);

  // Grouper par employé



  const groupedByEmployee = useMemo(() => {
    const groups = new Map();
    filteredPointages.forEach(p => {
      if (!groups.has(p.employee_id)) {
        groups.set(p.employee_id, {
          employeeId: p.employee_id,
          employeeName: p.employee_name,
          department: p.department,
          pointages: []
        });
      }
      groups.get(p.employee_id).pointages.push(p);
    });
    return Array.from(groups.values()).sort((a, b) => a.employeeName.localeCompare(b.employeeName));
  }, [filteredPointages]);



  // Stats filtrées

const filteredStats = useMemo(() => {
    return {
      total_employes: groupedByEmployee.length,
      total_pointages: filteredPointages.length,
      arrivees: filteredPointages.filter(p => p.type_pointage === 'arrivee').length,
      departs: filteredPointages.filter(p => p.type_pointage === 'depart').length,
      absents: filteredPointages.filter(p => p.statut === 'absent').length,
      envoyes: filteredPointages.filter(p => p.statut_comptabilite === 'envoye').length,
      photos: filteredPointages.filter(p => p.photo_pointage).length,


     incomplets: groupedByEmployee.filter(g => {
        if (!isEmployeeIncomplet(g.pointages)) return false;
        const pointagesRetardGrave = g.pointages.filter(p => 
          p.statut_ponctualite === 'retard_grave' || 
          p.statut_ponctualite === 'depart_anticipe_grave'
        );
        const retardsJustifies = pointagesRetardGrave.every(p => 
          justificationsSauvegardees?.[p.id] !== undefined ||
          (p.observations && p.observations.trim() !== '')
        );
        const globaleJustifiee = justificationsSauvegardees?.[`global_${g.employeeId}`] !== undefined ||
          !!justificationsGlobales?.[g.employeeId]?.texte;
        return !(retardsJustifies && globaleJustifiee);
      }).length

    };

    

  }, [filteredPointages, groupedByEmployee, justificationsSauvegardees, justificationsGlobales]);



  // Compter les résultats pour chaque filtre statut (badges)
  const filterCounts = useMemo(() => {
    // On applique employé + recherche mais PAS le filtre statut
    let base = pointagesHistorique;
    if (selectedEmployee) {
      base = base.filter(p => p.employee_id === selectedEmployee.id);
    }
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      base = base.filter(p =>
        p.employee_name.toLowerCase().includes(term) ||
        (p.department || '').toLowerCase().includes(term)
      );
    }


   // Grouper la base par employé pour calculer les incomplets
    const baseGroups = new Map();
    base.forEach(p => {
      if (!baseGroups.has(p.employee_id)) {
        baseGroups.set(p.employee_id, []);
      }
      baseGroups.get(p.employee_id).push(p);
    });

    return {
      tous: base.length,
      present: base.filter(p => p.statut === 'present').length,
      absent: base.filter(p => p.statut === 'absent').length,
      a_temps: base.filter(p => p.statut_ponctualite === 'a_temps').length,
      retard: base.filter(p => ['retard', 'retard_grave', 'tolerance'].includes(p.statut_ponctualite)).length,
      retard_grave: base.filter(p => p.statut_ponctualite === 'retard_grave').length,
      depart_anticipe: base.filter(p => ['depart_anticipe', 'depart_anticipe_grave'].includes(p.statut_ponctualite)).length,
      incomplet: Array.from(baseGroups.values()).filter(g => isEmployeeIncomplet(g)).length,



     justifie: Array.from(baseGroups.values()).filter(g => {
        if (!isEmployeeIncomplet(g)) return false;
        const pointagesRetardGrave = g.filter(p => 
          p.statut_ponctualite === 'retard_grave' || 
          p.statut_ponctualite === 'depart_anticipe_grave'
        );
        const retardsJustifies = pointagesRetardGrave.every(p => 
          justificationsSauvegardees?.[p.id] !== undefined ||
          (p.observations && p.observations.trim() !== '')
        );
        const globaleJustifiee = justificationsSauvegardees?.[`global_${g[0]?.employee_id}`] !== undefined ||
          !!justificationsGlobales?.[g[0]?.employee_id]?.texte;
        return retardsJustifies && globaleJustifiee;
      }).length,


      ok: Array.from(baseGroups.values()).filter(g => {
        const hasRetard = g.some(p => ['retard', 'retard_grave', 'tolerance', 'depart_anticipe', 'depart_anticipe_grave', 'pause_excessive'].includes(p.statut_ponctualite));
        const hasAbsent = g.some(p => p.statut === 'absent');
        return !hasRetard && !hasAbsent && !isEmployeeIncomplet(g);
      }).length,



    };


 }, [pointagesHistorique, selectedEmployee, searchTerm, justificationsSauvegardees, justificationsGlobales]);

  const hasActiveFilters = selectedEmployee || searchTerm || statutFilter !== 'tous';

  const handlePhotoClick = (url, name) => {
    setZoomPhoto({ url, name });
  };

  // Placeholder pour le téléchargement — sera développé plus tard
 const handleDownloadReport = async (type, value) => {
  try {
    showToast('Génération du rapport en cours...', 'loading');
    await api.pointages.telechargerRapport(selectedDate, type, value || '');
    showToast('Rapport téléchargé avec succès !', 'success');
  } catch (error) {
    console.error('❌ Erreur téléchargement rapport:', error);
    showToast('Erreur lors du téléchargement du rapport', 'error');
  }
};




const handleSaveJustification = async (employeeId, pointageId, texte) => {
    setSavingJustification(prev => ({ ...prev, [pointageId]: true }));
    try {
      if (String(pointageId).startsWith('global_')) {
        await api.pointages.saveJustificationGlobale(employeeId, selectedDate, texte);
      } else {
        await api.pointages.updateObservation(pointageId, texte);
      }


      // Marquer cette justification comme sauvegardée
     const now = new Date().toISOString();
      const savedInfo = {
        texte,
        created_by_name: user?.name || 'Inconnu',
        saved_at: now
      };

      setJustificationsSauvegardees(prev => ({ ...prev, [pointageId]: savedInfo }));

      // Mettre à jour justificationsGlobales immédiatement si c'est une justification globale
      if (String(pointageId).startsWith('global_')) {
        const empIdFromKey = Number(String(pointageId).replace('global_', ''));
        setJustificationsGlobales(prev => ({
          ...prev,
          [empIdFromKey]: {
            texte,
            created_by_name: user?.name || 'Inconnu',
            updated_at: now
          }
        }));
      }

      showToast('Justification enregistrée', 'success');



    } catch (error) {
      showToast('Erreur lors de la sauvegarde', 'error');
    } finally {
      setSavingJustification(prev => ({ ...prev, [pointageId]: false }));
    }
  };


  const handleJustificationChange = (pointageId, texte) => {
    setJustifications(prev => ({ ...prev, [pointageId]: texte }));
  };


  return (

    <div className="space-y-6">
      {/* STATISTIQUES */}
     {/* En-tête stats avec bouton guide */}
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wide">Vue d'ensemble</h2>
        <button
          onClick={() => setShowGuide(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-500 hover:text-[#448696] hover:bg-[#448696]/10 transition-all border border-gray-200 hover:border-[#448696]/30"
        >
          <HelpCircle size={14} />
          Guide de lecture
        </button>
      </div>

      <div className={`grid gap-3 ${
        comptaEnabled && photoEnabled 
          ? 'grid-cols-4 lg:grid-cols-8' 
          : comptaEnabled || photoEnabled 
          ? 'grid-cols-4 lg:grid-cols-7' 
          : 'grid-cols-4 lg:grid-cols-6'
      }`}>

      {[
          { label: 'Employés',   value: statsHistorique.total_employes,  icon: Users,         color: '#448696', filter: null },
          { label: 'Total',      value: statsHistorique.total_pointages,  icon: CheckCircle,   color: '#8b5cf6', filter: null },
          { label: 'Arrivées',   value: statsHistorique.arrivees,         icon: Fingerprint,   color: '#22c55e', filter: null },
          { label: 'Départs',    value: statsHistorique.departs,          icon: Fingerprint,   color: '#3b82f6', filter: null },
          { label: 'Absents',    value: statsHistorique.absents,          icon: X,             color: '#ef4444', filter: 'absent' },
          { label: 'Incomplets', value: statsHistorique.incomplets,       icon: AlertTriangle, color: '#f97316', filter: 'incomplet' },
          ...(comptaEnabled ? [{ label: 'Envoyés', value: statsHistorique.envoyes, icon: Send,   color: '#6366f1', filter: null }] : []),
          ...(photoEnabled  ? [{ label: 'Photos',  value: statsHistorique.photos,  icon: Camera, color: '#0ea5e9', filter: null }] : []),
        ].map(({ label, value, icon: Icon, color, filter }) => (


          <div
            key={label}
            onClick={() => filter && setStatutFilter(statutFilter === filter ? 'tous' : filter)}
            className={`bg-white rounded-2xl shadow-sm border p-3 transition-all ${
              filter ? 'cursor-pointer hover:shadow-md hover:-translate-y-0.5 active:scale-95' : ''
            } ${statutFilter === filter && filter ? 'border-2' : 'border-gray-100'}`}
            style={{ borderColor: statutFilter === filter && filter ? color : undefined }}
          >
            <div className="flex flex-col items-center gap-2 text-center">
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: color }}
              >
                <Icon size={16} className="text-white" />
              </div>
              <span className="text-xs font-semibold text-gray-500 leading-tight">{label}</span>
              <span className="text-2xl font-bold text-gray-900 leading-none">{value}</span>
              {filter && (
                <span className="text-[10px] text-gray-400 font-medium">↑ filtrer</span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* BARRE DE FILTRES */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 space-y-4">
        
        {/* Ligne 1 : Recherche employé + recherche texte + bouton télécharger */}
       <div className="flex items-start gap-4">
          <div className="flex-1">
            <div className="relative">


              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Recherhcer un employé..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  if (onSearchChange) onSearchChange(e.target.value);
                }}
                className="w-full pl-12 pr-10 py-3 bg-white border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#448696] font-medium text-sm transition-all"
              />
              {searchTerm && (
                <button 
                  onClick={() => {
                    setSearchTerm('');
                    if (onSearchChange) onSearchChange('');
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-gray-100 text-gray-400"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Bouton envoyer la page à la compta */}
            {comptaEnabled && pointagesHistorique.some(p => p.statut_comptabilite !== 'envoye') && (
              <button
                onClick={async () => {
                  const ids = pointagesHistorique
                    .filter(p => p.statut_comptabilite !== 'envoye')
                    .map(p => p.id);
                  if (ids.length === 0) return;
                  try {
                    showToast('Envoi en cours...', 'loading');
                    await onSendLotToAccounting(ids);
                  } catch (e) {
                    showToast('Erreur lors de l\'envoi', 'error');
                  }
                }}
                className="flex items-center gap-2 px-4 py-3 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all text-sm"
                style={{ backgroundColor: '#6366f1' }}
              >
                <Send size={16} />
                <span className="hidden md:inline">Envoyer la page</span>
                <span className="text-xs opacity-80">
                  ({pointagesHistorique.filter(p => p.statut_comptabilite !== 'envoye').length})
                </span>
              </button>
            )}
            <DownloadReportButton 
              departments={departments}
              onDownload={handleDownloadReport}
            />
          </div>
        </div>

        {/* Ligne 2 : Filtres par statut (chips) */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Filter size={14} className="text-gray-400" />
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Filtrer par statut</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {STATUT_FILTERS.map(({ key, label, icon: Icon, bgActive, bgInactive, textActive, textInactive }) => {
              const isActive = statutFilter === key;
              const count = filterCounts[key] || 0;

              return (
                <button
                  key={key}
                  onClick={() => setStatutFilter(isActive && key !== 'tous' ? 'tous' : key)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                    isActive ? `${bgActive} ${textActive} shadow-sm` : `${bgInactive} ${textInactive} hover:bg-gray-200`
                  }`}
                >
                  <Icon size={13} />
                  <span>{label}</span>
                  <span className={`ml-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                    isActive ? 'bg-white/25 text-white' : 'bg-gray-200 text-gray-500'
                  }`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Indicateur de filtre actif */}
        {hasActiveFilters && (
          <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
            <span className="text-xs text-gray-500">
              {filteredStats.total_employes} employé{filteredStats.total_employes > 1 ? 's' : ''} • {filteredStats.total_pointages} pointage{filteredStats.total_pointages > 1 ? 's' : ''}
            </span>
            <button
             onClick={() => {
                setSearchTerm('');
                setStatutFilter('tous');
                if (onSearchChange) onSearchChange('');
              }}
              className="text-xs text-[#448696] font-semibold hover:underline"
            >
              Réinitialiser les filtres
            </button>
          </div>
        )}
      </div>

     
        {/* LISTE GROUPÉE PAR EMPLOYÉ */}
    <div className="space-y-3">
      {loadingData ? null : groupedByEmployee.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
          <History className="w-16 h-16 mx-auto mb-4 text-gray-200" />
          <p className="text-lg font-semibold text-gray-400">Aucun pointage trouvé</p>
          <p className="text-sm text-gray-400 mt-1">
            {hasActiveFilters 
              ? 'Essayez de modifier vos filtres' 
              : 'Aucun pointage pour cette date'}
          </p>
        </div>
      ) : (
        groupedByEmployee.map(({ employeeId, employeeName, department, pointages }) => (

          <EmployeeCard
            key={employeeId}
            employeeName={employeeName}
            employeeId={employeeId}
            department={department}
            pointages={pointages}
            onSendToAccounting={onSendToAccounting}
            onSendLotToAccounting={onSendLotToAccounting}
            loading={loading}
            onPhotoClick={handlePhotoClick}
            comptaEnabled={comptaEnabled}
            isIncomplet={isEmployeeIncomplet(pointages)}
            justifications={justifications}
            savingJustification={savingJustification}
            onJustificationChange={handleJustificationChange}
            onSaveJustification={handleSaveJustification}
            justificationsGlobales={justificationsGlobales}
            justificationsSauvegardees={justificationsSauvegardees}
            typesAttendus={typesAttendus}
            photoEnabled={photoEnabled}
            companyTimezone={companyTimezone}
          />

        ))
      )}
    </div>

    {/* PAGINATION */}
    {paginationInfo.totalPages > 1 && (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">

      <Pagination
          page={pageCourante}
          totalPages={paginationInfo.totalPages}
          total={paginationInfo.total}
          limit={10}
          onPageChange={(p) => {
            onPageChange(p);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          itemLabel="employé"
        />
      </div>
    )}


      {/* POPUP ZOOM PHOTO */}
      {zoomPhoto && (
        <PhotoZoomPopup 
          photoUrl={zoomPhoto.url}
          employeeName={zoomPhoto.name}
          onClose={() => setZoomPhoto(null)}
        />
      )}


      {showGuide && (
    <GuideHistoriquePointages
      onClose={() => setShowGuide(false)}
      modePause={modePause}
      comptaEnabled={comptaEnabled}
      photoEnabled={photoEnabled}
    />
  )}
    </div>
  );
};

export default HistoriquePointages;
